import Swiper from "swiper";
import { Navigation, Manipulation } from "swiper/modules";
import {
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	hasClass,
	getHeightWithMargins,
} from "../../common/html";
import { NavigationOptions, SwiperOptions } from "swiper/types";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { debounce } from "../../utils/debounce";

const disposable: IDisposable = compositeDisposable([]);

export interface SliderComponent {
	dispose: () => void;
	sliderEl: HTMLElement;
	slideEls: HTMLElement[];
	addSlide: (index: number, slides: HTMLElement | string | Array<HTMLElement | string>) => void;
}

export const setTitleHeight = (swiper: Swiper) => {
	const slides = swiper.slides.map((slide, i) => swiper.slides[i]);
	let maxTitleHeight = 0;
	let slidesHaveDifferentHeights = false;

	for (let i = 0, l = slides.length; i < l; i++) {
		const slideEl = slides[i] as HTMLElement;
		const sliderTitleEl = expressQuerySelector<HTMLElement>(slideEl, ".technical-slide-title", false);
		if (!sliderTitleEl) continue;

		const titleHeight = getHeightWithMargins(sliderTitleEl);

		if (i == 0) maxTitleHeight = titleHeight;
		if (maxTitleHeight >= titleHeight) continue;

		slidesHaveDifferentHeights = true;
		maxTitleHeight = titleHeight;
	}

	// all slides are the same height, nothing to do
	if (!slidesHaveDifferentHeights) return;

	for (let i = 0, l = slides.length; i < l; i++) {
		const slideEl = slides[i] as HTMLElement;
		const sliderTitleEl = expressQuerySelector<HTMLElement>(slideEl, ".technical-slide-title", false);
		if (!sliderTitleEl) continue;

		sliderTitleEl.style.height = `${maxTitleHeight}px`;
	}
};

function setNavigationButtonHeight(sliderContainer: HTMLElement) {
	if (!sliderContainer) return null;

	const prevButton = expressQuerySelector<HTMLElement>(sliderContainer, ".technical-slider--navigation-prev");
	const nextButton = expressQuerySelector<HTMLElement>(sliderContainer, ".technical-slider--navigation-next");
	if (!prevButton && !nextButton) return;

	const firstImage = expressQuerySelector<HTMLElement>(
		sliderContainer,
		".technical-slider-slides-container .technical-slider-slide picture",
	);
	if (!firstImage) return;

	const sliderTop = sliderContainer.getBoundingClientRect().top;
	const imgTop = firstImage.getBoundingClientRect().top;
	const imgHeight = firstImage.getBoundingClientRect().height;
	const imgRelativeFromTop = imgTop - sliderTop;
	const calculatedTop = imgRelativeFromTop + imgHeight / 2;

	sliderContainer.style.setProperty("--slider-action-top", calculatedTop + "px");
}

function initSliders(): SliderComponent[] {
	const sliderContainers = expressQuerySelectorAll<HTMLElement>(document, ".technical-slider-container");
	return sliderContainers.map((el) => initSlider(el));
}

function getSettings(sliderEl: HTMLElement): SwiperOptions {
	let settings: SwiperOptions = {};

	if (sliderEl.dataset.sliderSettings) {
		settings = JSON.parse(sliderEl.dataset.sliderSettings);
		if (settings) return settings;
	}

	if (sliderEl.dataset.sliderSettingsEl) {
		const settingsEl = expressQuerySelector<HTMLElement>(document, sliderEl.dataset.sliderSettingsEl, false);
		if (settingsEl && settingsEl.dataset.sliderSettings) {
			settings = JSON.parse(settingsEl.dataset.sliderSettings);
			if (settings) return settings;
		}
	}

	return settings;
}

function initSlider(sliderEl: HTMLElement): SliderComponent {
	if (!sliderEl) return;
	if (!hasClass(sliderEl, "swiper")) sliderEl = expressQuerySelector<HTMLElement>(sliderEl, ".swiper");
	if (!sliderEl || hasClass(sliderEl, "swiper-initialized")) return;

	const settings: SwiperOptions = getSettings(sliderEl);

	const navigation = settings.navigation as NavigationOptions;
	if (navigation && navigation.enabled) {
		settings.modules = [Navigation, Manipulation]; // configure Swiper to use modules
	}

	// init Swiper
	const swiper = new Swiper(sliderEl, settings);

	const evl = expressEventListener(
		window,
		"resize",
		debounce(() => setNavigationButtonHeight(sliderEl), 250),
	);
	disposable.add(evl);

	setNavigationButtonHeight(swiper.hostEl);

	setTitleHeight(swiper);

	return {
		dispose: disposable.dispose,
		sliderEl: swiper.hostEl,
		slideEls: swiper.slides.map((slide, i) => swiper.slides[i]),
		addSlide: (index: number, slides: HTMLElement | string | Array<HTMLElement | string>) => {
			swiper.addSlide(index, slides);
			swiper.update();
			swiper.slideTo(0);
		},
	};
}

window["initSlider"] = initSlider;

export { initSliders, initSlider };
